import styled from '@emotion/styled';
import { faXmark } from '@fortawesome/duotone-regular-svg-icons';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog from '@mui/material/Dialog';
import type { DialogProps } from '@mui/material/Dialog';
import Popover from '@mui/material/Popover';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import CloseButton from '~/common/mui/Buttons/MUIButton/MUIIconButton';
import { hexToRgba } from '~/common/util/format';
import palette from '~/theme/mui/palette';

import MUIDialogActions from './MUIDialogActions';
import MUIDialogContent from './MUIDialogContent';
import MUIDialogTitle from './MUIDialogTitle';

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

const PositionMap = {
  bottom: {
    container: {
      justifyContent: 'center',
      alignItems: 'flex-end',
      paddingLeft: '17rem',
    },
    paper: { marginBottom: '5rem' },
  },
  left: {
    container: { justifyContent: 'flex-start', alignItems: 'center' },
    paper: { marginLeft: '7rem' },
  },
  center: {
    container: { justifyContent: 'center', alignItems: 'center' },
    paper: {},
  },
} as const;

type PrimaryButtonProps = {
  isSubmitting?: boolean;
  isDisabled?: boolean;
  name?: string;
  onSubmit: React.MouseEventHandler<HTMLButtonElement> | (() => Promise<void>);
};

type BaseDialogProps = {
  icon?: IconProp;
  iconColor?: string;
  dividers?: boolean;
  title: string;
  contentSx?: Pick<React.ComponentProps<typeof MUIDialogContent>, 'sx'>['sx'];
  paperSx?: CSSProperties;
  headerControls?: ReactNode;
  onClose: () => void;
  dialogPosition: keyof typeof PositionMap;
  anchorSelector?: string | null;
  children: ReactNode;
} & DialogProps;

type DialogWithActions = BaseDialogProps & {
  actions?: ReactNode;
  primaryButton?: never;
  secondaryButton?: never;
};

type DialogWithStandardButtons = BaseDialogProps & {
  actions?: never;
  primaryButton?: PrimaryButtonProps;
  secondaryButton?: ReactNode;
};

type MUIDialogProps = DialogWithActions | DialogWithStandardButtons;

/**
 * MUIDialog is a composition of Material UI's Dialog, DialogTitle, DialogContent, and DialogActions allowing for flexible dialog configurations.
 *
 * - If you want dialog actions for your footer, you can optionally provide either `actions` (for custom footer actions scenarios) or `primaryButton` (for a single primary action), but not both.
 * - The `secondaryButton` prop can be used alongside `primaryButton` to provide a secondary action button.
 * - The `dialogPosition` prop allows you to position the dialog at the bottom, left, or center of the screen.
 *
 * @example <caption>Dialog with custom actions</caption>
 * <MUIDialog
 *   open={true}
 *   onClose={() => {}}
 *   title="Custom Actions Dialog"
 *   dialogPosition="bottom"
 *   actions={
 *     <>
 *       <Button onClick={() => {}}>Cancel</Button>
 *       <Button onClick={() => {}}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <div>This dialog is bottom-aligned.</div>
 * </MUIDialog>
 *
 * @example <caption>Dialog with a primary button</caption>
 * <MUIDialog
 *   open={true}
 *   onClose={() => {}}
 *   title="Primary Button Dialog"
 *   dialogPosition="left"
 *   primaryButton={{
 *     name: 'Save',
 *     onSubmit: () => {},
 *     isSubmitting: false,
 *     isDisabled: false,
 *   }}
 * >
 *   <div>This dialog is left-aligned.</div>
 * </MUIDialog>
 */
const MUIDialog = ({
  icon,
  iconColor,
  title,
  actions,
  open,
  onClose,
  paperSx,
  contentSx,
  dialogPosition,
  headerControls,
  dividers = false,
  primaryButton,
  secondaryButton,
  children,
  hideBackdrop,
  anchorSelector,
  ...dialogProps
}: MUIDialogProps) => {
  // Pass a function to MUI Popover rather than a DOM element.
  // MUI calls this function at positioning time (post-commit, stable DOM),
  // so getBoundingClientRect() always reflects the final layout.
  const anchorElFn = useMemo(
    () =>
      anchorSelector
        ? () => document.querySelector<HTMLElement>(anchorSelector)
        : undefined,
    [anchorSelector],
  );

  const { className, ...restDialogProps } = dialogProps;
  const mergedClassName = ['mui-dialog-root', className]
    .filter(Boolean)
    .join(' ');

  // MUI Modal always sets aria-hidden="true" on #root when any modal/popover opens,
  // but this Popover is intentionally non-modal (background remains interactive).
  // Leaving aria-hidden on #root while focusable elements exist violates WCAG 4.1.3.
  // We use a MutationObserver to immediately strip aria-hidden from #root while open.
  useEffect(() => {
    if (!open || !anchorElFn) return;

    const root = document.getElementById('root');
    if (!root) return;

    const observer = new MutationObserver(() => {
      if (root.hasAttribute('aria-hidden')) {
        root.removeAttribute('aria-hidden');
      }
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
    root.removeAttribute('aria-hidden');

    return () => observer.disconnect();
  }, [open, anchorElFn]);

  const paperStyles = {
    ...paperSx,
    position: 'relative' as const,
    borderRadius: '16px',
    paddingBottom: actions ? 0 : '1.5rem',
    boxShadow: `
      0 1px 18px 0 ${hexToRgba(palette.black, 0.12)},
      0 6px 10px 0 ${hexToRgba(palette.black, 0.14)},
      0 3px 5px -1px ${hexToRgba(palette.black, 0.2)}
    `,
  };

  const innerContent = (
    <>
      <MUIDialogTitle
        color={palette.blue[900]}
        sx={{
          fontSize: '1.5rem',
          fontWeight: 500,
          padding: '1.5rem 1.5rem 1rem 1.5rem',
        }}
      >
        {icon && (
          <StyledIcon aria-label="dialog icon" icon={icon} color={iconColor} />
        )}
        {title}
        <CloseButton
          aria-label="dialog close button"
          sx={{
            position: 'absolute',
            top: '6px',
            right: '0.7rem',
            padding: '1rem',
            minWidth: '2.5rem',
            minHeight: '2.5rem',
            width: 'auto',
            height: 'auto',
          }}
          onClick={(e) => onClose?.(e, 'backdropClick')}
        >
          <FontAwesomeIcon
            style={{ fontSize: '1rem' }}
            color={palette.black}
            icon={faXmark}
          />
        </CloseButton>
        {headerControls}
      </MUIDialogTitle>
      <MUIDialogContent dividers={dividers} sx={contentSx}>
        {children}
      </MUIDialogContent>
      {actions && (
        <MUIDialogActions sx={{ padding: 0 }}>{actions}</MUIDialogActions>
      )}
      {primaryButton && (
        <MUIDialogActions
          sx={{
            padding: '1rem 1.5rem 0 1.5rem',
            justifyContent: secondaryButton ? 'space-between' : 'flex-end',
          }}
        >
          {secondaryButton}
          <Button
            isLoading={primaryButton.isSubmitting}
            isDisabled={primaryButton.isDisabled}
            onClick={primaryButton.onSubmit}
          >
            {primaryButton.name || 'Save'}
          </Button>
        </MUIDialogActions>
      )}
    </>
  );

  if (anchorElFn) {
    return (
      <Popover
        open={open}
        anchorEl={anchorElFn}
        onClose={() => {}}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableScrollLock
        disablePortal={false}
        hideBackdrop
        transitionDuration={0}
        // Min distance from bottom edge
        // repositions it. Action bar is 56px tall + 4px from bottom = 60px,
        marginThreshold={84}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': { pointerEvents: 'auto' },
        }}
        slotProps={{
          paper: {
            sx: {
              width: 'fit-content',
              display: 'flex',
              flexDirection: 'column',
              ...paperStyles,
              marginTop: '8px',
              marginLeft: '1.5rem',
            },
          },
        }}
      >
        {innerContent}
      </Popover>
    );
  }

  return (
    <Dialog
      open={open}
      className={mergedClassName}
      slotProps={{
        paper: {
          sx: {
            ...paperSx,
            ...PositionMap[dialogPosition]?.paper,
            position: 'relative',
            borderRadius: '16px',
            paddingBottom: actions ? 0 : '1.5rem',
            boxShadow: `
              0 1px 18px 0 ${hexToRgba(palette.black, 0.12)},
              0 6px 10px 0 ${hexToRgba(palette.black, 0.14)},
              0 3px 5px -1px ${hexToRgba(palette.black, 0.2)}
            `,
          },
        },
      }}
      hideBackdrop={hideBackdrop ?? true}
      disableEnforceFocus
      disableScrollLock // Prevents scroll jump when dialog opens
      disablePortal={false}
      maxWidth={false}
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-paper': { pointerEvents: 'auto' },
        '& .MuiDialog-container': {
          ...PositionMap[dialogPosition]?.container,
        },
      }}
      {...restDialogProps}
    >
      {innerContent}
    </Dialog>
  );
};

export default MUIDialog;
